/**
 * Replays flash.display.Graphics commands (src/flash/display/Graphics.ts GraphicsCommand list) on a
 * Canvas 2D context.
 *
 * Flash drawing-API semantics reproduced:
 * - fills use the even-odd rule; every fill subpath is closed implicitly; a fill starts at the
 *   current pen position; moveTo inside a fill starts a new subpath;
 * - all fills of a Graphics are drawn first (in order), then all strokes (in order) — lines are on
 *   top of fills, like Ruffle's drawing builder;
 * - lineStyle(NaN / undefined) disables strokes; thickness 0 = hairline; strokes thinner than one
 *   device pixel are drawn one pixel wide; scaleMode "none"/"horizontal"/"vertical" keep the
 *   thickness in device pixels (approximation: uniform scale);
 * - caps/joints default to round; colour transforms apply to colours and gradient stops;
 * - gradient fills map the Flash gradient square (-819.2..819.2) through the gradient matrix;
 *   spreadMethod reflect/repeat and interpolationMethod linearRGB are drawn as pad/RGB
 *   (UNVERIFIED approximation); bitmap fills use the fill matrix, repeat and smoothing flags
 *   (non-repeating fills are not edge-clamped).
 */
import type { GraphicsCommand } from '../display/Graphics';
import { CT, ColorStages, Mat, cssColor, isAlphaOnlyCT, isIdentityCT, matScale, mul } from './math';
import type { Surfaces } from './surfaces';

type PathOp = { t: 'M'; x: number; y: number } | { t: 'L'; x: number; y: number } | { t: 'Q'; cx: number; cy: number; x: number; y: number };

type FillStyle =
  | { kind: 'solid'; color: number; alpha: number }
  | { kind: 'gradient'; cmd: Extract<GraphicsCommand, { op: 'beginGradientFill' }> }
  | { kind: 'bitmap'; cmd: Extract<GraphicsCommand, { op: 'beginBitmapFill' }> };

interface LineStyle {
  thickness: number;
  color: number;
  alpha: number;
  scaleMode: string;
  caps: string | null;
  joints: string | null;
  miterLimit: number;
}

interface FillItem {
  style: FillStyle;
  path: PathOp[];
}
interface StrokeItem {
  style: LineStyle;
  path: PathOp[];
}

/** Split commands into fills and strokes (Flash drawing-API model). */
export function buildGraphics(cmds: readonly GraphicsCommand[]): { fills: FillItem[]; strokes: StrokeItem[] } {
  const fills: FillItem[] = [];
  const strokes: StrokeItem[] = [];
  // (assertions: TS does not track assignments made inside the helper closures)
  let fill = null as FillItem | null;
  let stroke = null as StrokeItem | null;
  let line = null as LineStyle | null;
  let px = 0;
  let py = 0;
  const closeFill = () => {
    if (fill && fill.path.length > 1) fills.push(fill);
    fill = null;
  };
  const closeStroke = () => {
    if (stroke && stroke.path.some((p) => p.t !== 'M')) strokes.push(stroke);
    stroke = null;
  };
  const ensureStroke = () => {
    if (!line) return;
    if (!stroke) stroke = { style: line, path: [{ t: 'M', x: px, y: py }] };
  };
  for (const c of cmds) {
    switch (c.op) {
      case 'beginFill':
        closeFill();
        fill = { style: { kind: 'solid', color: c.color, alpha: c.alpha }, path: [{ t: 'M', x: px, y: py }] };
        break;
      case 'beginGradientFill':
        closeFill();
        fill = { style: { kind: 'gradient', cmd: c }, path: [{ t: 'M', x: px, y: py }] };
        break;
      case 'beginBitmapFill':
        closeFill();
        fill = { style: { kind: 'bitmap', cmd: c }, path: [{ t: 'M', x: px, y: py }] };
        break;
      case 'endFill':
        closeFill();
        break;
      case 'lineStyle':
        closeStroke();
        line =
          c.thickness === c.thickness
            ? { thickness: c.thickness, color: c.color, alpha: c.alpha, scaleMode: c.scaleMode, caps: c.caps, joints: c.joints, miterLimit: c.miterLimit }
            : null;
        break;
      case 'moveTo':
        px = c.x;
        py = c.y;
        if (fill) fill.path.push({ t: 'M', x: px, y: py });
        if (stroke) stroke.path.push({ t: 'M', x: px, y: py });
        break;
      case 'lineTo':
        ensureStroke();
        px = c.x;
        py = c.y;
        if (fill) fill.path.push({ t: 'L', x: px, y: py });
        if (stroke) stroke.path.push({ t: 'L', x: px, y: py });
        break;
      case 'curveTo':
        ensureStroke();
        px = c.x;
        py = c.y;
        if (fill) fill.path.push({ t: 'Q', cx: c.cx, cy: c.cy, x: px, y: py });
        if (stroke) stroke.path.push({ t: 'Q', cx: c.cx, cy: c.cy, x: px, y: py });
        break;
    }
  }
  closeFill();
  closeStroke();
  return { fills, strokes };
}

function tracePath(ctx: CanvasRenderingContext2D, path: PathOp[]): void {
  ctx.beginPath();
  for (const p of path) {
    if (p.t === 'M') ctx.moveTo(p.x, p.y);
    else if (p.t === 'L') ctx.lineTo(p.x, p.y);
    else ctx.quadraticCurveTo(p.cx, p.cy, p.x, p.y);
  }
}

const HUGE = 1e5;

/**
 * Draw Graphics commands with object->device matrix `m`, colour transform `ct` and outer colour
 * stages (distributed colour-matrix filters). `mask`: drawing a mask (colours irrelevant, opaque).
 */
export function drawGraphics(ctx: CanvasRenderingContext2D, cmds: readonly GraphicsCommand[], m: Mat, ct: CT, surfaces: Surfaces, mask = false, outer: ColorStages | null = null): void {
  if (!cmds.length) return;
  const { fills, strokes } = buildGraphics(cmds);
  const scale = matScale(m) || 1;
  ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  ctx.globalAlpha = 1;
  for (const f of fills) {
    const st = f.style;
    if (st.kind === 'solid' || mask) {
      tracePath(ctx, f.path);
      ctx.fillStyle = mask ? '#000' : cssColor(st.kind === 'solid' ? st.color : 0, st.kind === 'solid' ? st.alpha : 1, ct, outer);
      ctx.fill('evenodd');
      continue;
    }
    ctx.save();
    tracePath(ctx, f.path);
    ctx.clip('evenodd');
    if (st.kind === 'gradient') {
      const g = st.cmd;
      const gm = g.matrix ? ([g.matrix.a, g.matrix.b, g.matrix.c, g.matrix.d, g.matrix.tx, g.matrix.ty] as Mat) : ([1, 0, 0, 1, 0, 0] as Mat);
      const full = mul(m, gm);
      ctx.setTransform(full[0], full[1], full[2], full[3], full[4], full[5]);
      const grad =
        g.type === 'radial'
          ? ctx.createRadialGradient(Math.max(-1, Math.min(1, g.focalPointRatio || 0)) * 819.2, 0, 0, 0, 0, 819.2)
          : ctx.createLinearGradient(-819.2, 0, 819.2, 0);
      const n = Math.min(g.colors.length, g.alphas.length, g.ratios.length);
      for (let i = 0; i < n; i++) {
        const r = Math.max(0, Math.min(255, g.ratios[i])) / 255;
        grad.addColorStop(r, cssColor(g.colors[i] >>> 0, +g.alphas[i], ct, outer));
      }
      ctx.fillStyle = grad;
      ctx.fillRect(-HUGE, -HUGE, 2 * HUGE, 2 * HUGE);
    } else {
      const b = st.cmd;
      const bd: any = b.bitmap;
      if (bd && !bd.$disposed) {
        const plain = !outer && (isIdentityCT(ct) || isAlphaOnlyCT(ct));
        const tex = plain ? surfaces.bitmap(bd) : surfaces.tintedBitmap(bd, ct, outer);
        const bm = b.matrix ? ([b.matrix.a, b.matrix.b, b.matrix.c, b.matrix.d, b.matrix.tx, b.matrix.ty] as Mat) : ([1, 0, 0, 1, 0, 0] as Mat);
        const full = mul(m, bm);
        ctx.setTransform(full[0], full[1], full[2], full[3], full[4], full[5]);
        ctx.imageSmoothingEnabled = !!b.smooth;
        ctx.globalAlpha = plain && isAlphaOnlyCT(ct) ? ct[3] : 1;
        if (b.repeat) {
          const pat = ctx.createPattern(tex, 'repeat');
          if (pat) {
            ctx.fillStyle = pat;
            ctx.fillRect(-HUGE, -HUGE, 2 * HUGE, 2 * HUGE);
          }
        } else ctx.drawImage(tex, 0, 0);
        ctx.globalAlpha = 1;
      }
    }
    ctx.restore();
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  }
  for (const s of strokes) {
    const ls = s.style;
    let width: number;
    if (ls.thickness <= 0) width = 1 / scale; // hairline
    else if (ls.scaleMode === 'normal' || !ls.scaleMode) width = Math.max(ls.thickness, 1 / scale);
    else width = Math.max(ls.thickness, 1) / scale;
    ctx.lineWidth = width;
    ctx.lineCap = ls.caps === 'none' ? 'butt' : ls.caps === 'square' ? 'square' : 'round';
    ctx.lineJoin = ls.joints === 'miter' ? 'miter' : ls.joints === 'bevel' ? 'bevel' : 'round';
    ctx.miterLimit = Math.max(1, ls.miterLimit || 3);
    ctx.strokeStyle = mask ? '#000' : cssColor(ls.color, ls.alpha, ct, outer);
    tracePath(ctx, s.path);
    ctx.stroke();
  }
}
